import { useState, useEffect, useCallback, useRef } from "react";
import { CircleMarker, Marker, Tooltip, useMapEvents } from "react-leaflet";
import L from "leaflet";
import api from "../../api/client";

/**
 * NAP Overlay for the NI Map View.
 * Renders NAP point markers when zoom >= 16 and toggle is ON.
 * Fetches points within visible bounds with debouncing.
 *
 * Props:
 *  - enabled: boolean — whether the NAP toggle is ON
 *  - onPointClick: (point) => void — called when a NAP marker is clicked
 *  - selectedNapId: number|null — id of currently selected NAP (for highlight)
 */
export default function NapOverlay({ enabled, onPointClick, selectedNapId }) {
  const [zoom, setZoom] = useState(0);
  const [napPoints, setNapPoints] = useState([]);
  const [spiderfied, setSpiderfied] = useState(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const prevBoundsKey = useRef("");

  const map = useMapEvents({
    zoomend() {
      setZoom(map.getZoom());
      loadIfNeeded(map.getZoom(), map.getBounds());
    },
    moveend() {
      loadIfNeeded(map.getZoom(), map.getBounds());
    },
  });

  // Initialize zoom on mount
  useEffect(() => {
    setZoom(map.getZoom());
  }, [map]);

  const loadIfNeeded = useCallback((currentZoom, bounds) => {
    if (!enabled || currentZoom < 16) {
      return;
    }
    // Debounce
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchPoints(bounds);
    }, 400);
  }, [enabled]);

  const fetchPoints = useCallback(async (bounds) => {
    if (!bounds) return;
    const north = bounds.getNorth();
    const south = bounds.getSouth();
    const east = bounds.getEast();
    const west = bounds.getWest();
    const key = `${north.toFixed(5)}_${south.toFixed(5)}_${east.toFixed(5)}_${west.toFixed(5)}`;
    if (key === prevBoundsKey.current) return;
    prevBoundsKey.current = key;

    // Cancel previous request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const { data } = await api.get("/nap-locator/map-points-bounds", {
        params: { north, south, east, west },
        signal: controller.signal,
      });
      if (!controller.signal.aborted) {
        setNapPoints(data);
      }
    } catch (err) {
      if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
        // Silently fail on network errors
      }
    }
  }, []);

  // Clear points when disabled or zoom < 16
  useEffect(() => {
    if (!enabled || zoom < 16) {
      setNapPoints([]);
      prevBoundsKey.current = "";
    } else {
      // Load on enable
      loadIfNeeded(map.getZoom(), map.getBounds());
    }
  }, [enabled, zoom, map, loadIfNeeded]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // Clear spiderfied state on zoom change
  useEffect(() => {
    setSpiderfied(null);
  }, [zoom]);

  // Spiderfy click handler
  const handleMarkerClick = useCallback((pt) => {
    const key = `${pt.latitude.toFixed(5)}_${pt.longitude.toFixed(5)}`;
    // Build location groups inline for check
    const group = napPoints.filter(
      (p) => p.latitude.toFixed(5) === pt.latitude.toFixed(5) && p.longitude.toFixed(5) === pt.longitude.toFixed(5)
    );
    if (group.length >= 2) {
      // Spread points in a circle
      const radius = 0.00015;
      const angleStep = (2 * Math.PI) / group.length;
      const spread = group.map((p, i) => ({
        ...p,
        spiderLat: pt.latitude + radius * Math.cos(i * angleStep),
        spiderLng: pt.longitude + radius * Math.sin(i * angleStep),
      }));
      setSpiderfied({ center: [pt.latitude, pt.longitude], points: spread });
    } else {
      setSpiderfied(null);
      onPointClick(pt);
    }
  }, [napPoints, onPointClick]);

  if (!enabled || zoom < 16 || napPoints.length === 0) return null;

  // Group points by location for overlap detection (same as NAP Locator)
  const locationGroups = {};
  for (const pt of napPoints) {
    const key = `${pt.latitude.toFixed(5)}_${pt.longitude.toFixed(5)}`;
    if (!locationGroups[key]) locationGroups[key] = [];
    locationGroups[key].push(pt);
  }

  // Multi-point groups for count badges
  const multiGroups = [];
  const seenKeys = new Set();
  for (const pt of napPoints) {
    const key = `${pt.latitude.toFixed(5)}_${pt.longitude.toFixed(5)}`;
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);
    const group = locationGroups[key];
    if (group.length >= 2) {
      multiGroups.push({ key, latitude: pt.latitude, longitude: pt.longitude, count: group.length });
    }
  }

  return (
    <>
      {napPoints.map((pt) => {
        let fillColor = "#eab308"; // yellow - Not Yet Updated
        if (pt.stencil === "N") {
          fillColor = "#3b82f6"; // blue - No Record
        } else if (pt.stencil === "Y" && pt.vacant === 0) {
          fillColor = "#ef4444"; // red - Full NAP
        } else if (pt.stencil === "Y" && pt.vacant >= 1) {
          fillColor = "#22c55e"; // green - Available
        }
        if (pt.manual_full_nap) fillColor = "#ef4444"; // manually flagged Full NAP overrides
        const isSelected = selectedNapId === pt.id;
        return (
          <CircleMarker
            key={`nap-ni-${pt.id}`}
            center={[pt.latitude, pt.longitude]}
            radius={isSelected ? 8 : 5}
            pathOptions={{
              fillColor,
              fillOpacity: 0.85,
              color: isSelected ? "#ef4444" : "#fff",
              weight: isSelected ? 3 : 1,
            }}
            eventHandlers={{
              click: () => handleMarkerClick(pt),
            }}
          >
            <Tooltip direction="top" offset={[0, -5]} opacity={0.9}>
              <div className="text-xs">
                <div className="font-semibold">{pt.nap_code}</div>
                <div className="text-gray-600">{pt.area || "—"}</div>
                {pt.stencil === "Y" && (
                  <div className="text-gray-500 mt-0.5 border-t pt-0.5">
                    <span className="text-red-500">Used: {pt.used_port ?? "—"}</span>{" | "}
                    <span className="text-green-600">Vacant: {pt.vacant ?? "—"}</span>{" | "}
                    <span className="text-orange-600">ONU: {pt.onu_connect ?? "—"}</span>
                  </div>
                )}
              </div>
            </Tooltip>
          </CircleMarker>
        );
      })}

      {/* Count badges for overlapping points */}
      {multiGroups.map((g) => {
        // Hide only the badge for the currently spiderfied group
        if (spiderfied) {
          const spiderKey = `${spiderfied.center[0].toFixed(5)}_${spiderfied.center[1].toFixed(5)}`;
          if (g.key === spiderKey) return null;
        }
        return (
          <Marker
            key={`nap-ni-count-${g.key}`}
            position={[g.latitude, g.longitude]}
            interactive={false}
            icon={L.divIcon({
              className: "",
              html: `<div style="color:#1f2937;font-size:8px;font-weight:700;text-shadow:0 0 2px rgba(255,255,255,0.9);pointer-events:none;text-align:center;line-height:10px;">${g.count}</div>`,
              iconSize: [10, 10],
              iconAnchor: [5, 5],
            })}
          />
        );
      })}

      {/* Vacant port labels at zoom >= 18 (only for single-point locations) */}
      {zoom >= 18 && napPoints.map((pt) => {
        const key = `${pt.latitude.toFixed(5)}_${pt.longitude.toFixed(5)}`;
        const group = locationGroups[key];
        if (group && group.length > 1) return null;
        if (pt.vacant == null || pt.vacant === 0 || pt.stencil !== "Y" || pt.manual_full_nap) return null;
        return (
          <Marker
            key={`nap-ni-vacant-${pt.id}`}
            position={[pt.latitude, pt.longitude]}
            interactive={false}
            icon={L.divIcon({
              className: "",
              html: `<div style="position:absolute;left:8px;top:-8px;white-space:nowrap;background:#166534;color:#fff;font-size:10px;font-weight:700;padding:1px 4px;border-radius:8px;pointer-events:none;box-shadow:0 1px 3px rgba(0,0,0,0.3);">${pt.vacant}</div>`,
              iconSize: [0, 0],
              iconAnchor: [0, 0],
            })}
          />
        );
      })}

      {/* Vacant port total for multi-point groups at zoom >= 18 */}
      {zoom >= 18 && (() => {
        const rendered = new Set();
        return napPoints.map((pt) => {
          const key = `${pt.latitude.toFixed(5)}_${pt.longitude.toFixed(5)}`;
          const group = locationGroups[key];
          if (!group || group.length < 2) return null;
          if (rendered.has(key)) return null;
          rendered.add(key);
          const totalVacant = group.reduce((sum, p) => sum + (p.stencil === "Y" && p.vacant != null ? p.vacant : 0), 0);
          if (totalVacant === 0) return null;
          return (
            <Marker
              key={`nap-ni-vacant-grp-${key}`}
              position={[pt.latitude, pt.longitude]}
              interactive={false}
              icon={L.divIcon({
                className: "",
                html: `<div style="position:absolute;left:8px;top:5px;white-space:nowrap;background:#166534;color:#fff;font-size:10px;font-weight:700;padding:1px 4px;border-radius:8px;pointer-events:none;box-shadow:0 1px 3px rgba(0,0,0,0.3);">${totalVacant}</div>`,
                iconSize: [0, 0],
                iconAnchor: [0, 0],
              })}
            />
          );
        });
      })()}

      {/* Spiderfied markers */}
      {spiderfied && spiderfied.points.map((pt) => {
        let fillColor = "#eab308";
        if (pt.stencil === "N") fillColor = "#3b82f6";
        else if (pt.stencil === "Y" && pt.vacant === 0) fillColor = "#ef4444";
        else if (pt.stencil === "Y" && pt.vacant >= 1) fillColor = "#22c55e";
        if (pt.manual_full_nap) fillColor = "#ef4444"; // manually flagged Full NAP overrides
        return (
          <CircleMarker
            key={`nap-ni-spider-${pt.id}`}
            center={[pt.spiderLat, pt.spiderLng]}
            radius={7}
            pathOptions={{
              fillColor,
              fillOpacity: 0.9,
              color: "#1e40af",
              weight: 2,
            }}
            eventHandlers={{
              click: () => {
                setSpiderfied(null);
                onPointClick(pt);
              },
            }}
          >
            <Tooltip direction="top" offset={[0, -5]} opacity={0.9}>
              <div className="text-xs">
                <div className="font-semibold">{pt.nap_code}</div>
                <div className="text-gray-600">{pt.area || "—"}</div>
                {pt.stencil === "Y" && (
                  <div className="text-gray-500 mt-0.5 border-t pt-0.5">
                    <span className="text-red-500">Used: {pt.used_port ?? "—"}</span>{" | "}
                    <span className="text-green-600">Vacant: {pt.vacant ?? "—"}</span>{" | "}
                    <span className="text-orange-600">ONU: {pt.onu_connect ?? "—"}</span>
                  </div>
                )}
              </div>
            </Tooltip>
          </CircleMarker>
        );
      })}

      {/* Spiderfied vacant labels at zoom >= 18 */}
      {spiderfied && zoom >= 18 && spiderfied.points.map((pt) => {
        if (pt.vacant == null || pt.vacant === 0 || pt.stencil !== "Y" || pt.manual_full_nap) return null;
        return (
          <Marker
            key={`nap-ni-spider-vacant-${pt.id}`}
            position={[pt.spiderLat, pt.spiderLng]}
            interactive={false}
            icon={L.divIcon({
              className: "",
              html: `<div style="position:absolute;left:8px;top:-8px;white-space:nowrap;background:#166534;color:#fff;font-size:10px;font-weight:700;padding:1px 4px;border-radius:8px;pointer-events:none;box-shadow:0 1px 3px rgba(0,0,0,0.3);">${pt.vacant}</div>`,
              iconSize: [0, 0],
              iconAnchor: [0, 0],
            })}
          />
        );
      })}

      {/* Selected NAP pulse ring */}
      {selectedNapId && (() => {
        const sel = napPoints.find((p) => p.id === selectedNapId);
        if (!sel) return null;
        return (
          <Marker
            key="nap-ni-pulse"
            position={[sel.latitude, sel.longitude]}
            interactive={false}
            icon={L.divIcon({
              className: "",
              html: `<div style="width:20px;height:20px;border-radius:50%;border:3px solid #ef4444;background:transparent;animation:nap-ni-pulse-ring 1.5s ease-out infinite;pointer-events:none;"></div>`,
              iconSize: [20, 20],
              iconAnchor: [10, 10],
            })}
          />
        );
      })()}
    </>
  );
}
